import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Settings, X, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const STORAGE_KEY = "galvani_cookie_consent";
const CONSENT_VERSION = "1.0";

/**
 * Read the current consent from localStorage.
 * Returns null if user has never made a choice.
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function emitConsentChange(consent) {
  window.dispatchEvent(new CustomEvent("cookie-consent-change", { detail: consent }));
}

export default function CookieBanner() {
  // "hidden" | "banner" | "settings"
  const [view, setView] = useState("hidden");
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // On mount: if no consent yet, show banner (skip on /admin routes)
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      return;
    }
    const existing = getCookieConsent();
    if (!existing) {
      const t = setTimeout(() => setView("banner"), 600);
      return () => clearTimeout(t);
    } else {
      setPreferences({
        necessary: true,
        analytics: !!existing.analytics,
        marketing: !!existing.marketing,
      });
    }

    // Allow external triggers (floating button) to re-open the settings modal
    const handler = () => {
      const current = getCookieConsent();
      if (current) {
        setPreferences({
          necessary: true,
          analytics: !!current.analytics,
          marketing: !!current.marketing,
        });
      }
      setView("settings");
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const saveConsent = (prefs) => {
    const payload = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      ...prefs,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    emitConsentChange(payload);
    setView("hidden");
  };

  const handleAcceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const handleRejectAll = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const handleSavePreferences = () => saveConsent(preferences);

  return (
    <>
      <AnimatePresence>
        {/* Main banner */}
        {view === "banner" && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
            data-testid="cookie-banner"
          >
            <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                  <div className="flex gap-4 flex-1">
                    <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 items-center justify-center flex-shrink-0">
                      <Cookie className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-galvani-text text-lg mb-2">
                        Tu privacidad es importante
                      </h3>
                      <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                        Utilizamos cookies propias y de terceros para analizar el uso de la web y mostrarte
                        contenido personalizado. Puedes aceptar todas, rechazarlas o configurarlas según tus
                        preferencias. Consulta nuestra{" "}
                        <a
                          href="/politica-cookies"
                          className="text-galvani-purple hover:underline font-medium"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Política de Cookies
                        </a>{" "}
                        para más información.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 w-full lg:w-auto lg:min-w-[200px]">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold rounded-full h-11 px-6 shadow-md"
                      data-testid="cookie-accept-all-btn"
                    >
                      Aceptar todas
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="border-slate-300 text-galvani-text hover:bg-slate-50 font-body font-semibold rounded-full h-11 px-6"
                      data-testid="cookie-reject-all-btn"
                    >
                      Rechazar todas
                    </Button>
                    <Button
                      onClick={() => setView("settings")}
                      variant="ghost"
                      className="text-galvani-purple hover:bg-purple-50 font-body font-medium rounded-full h-11 px-6"
                      data-testid="cookie-configure-btn"
                    >
                      <Settings size={16} className="mr-2" />
                      Configurar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Settings modal */}
        {view === "settings" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[101] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="cookie-settings-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget && getCookieConsent()) setView("hidden");
            }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 md:px-8 py-5 flex items-center justify-between z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-galvani-text text-xl">
                    Configuración de cookies
                  </h3>
                </div>
                {getCookieConsent() && (
                  <button
                    onClick={() => setView("hidden")}
                    className="text-slate-400 hover:text-galvani-text transition-colors p-2"
                    aria-label="Cerrar"
                    data-testid="cookie-settings-close-btn"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="p-6 md:p-8 space-y-4">
                <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                  Activa o desactiva los distintos tipos de cookies según tus preferencias. Las cookies
                  técnicas son imprescindibles y no se pueden desactivar.
                </p>

                <div className="p-5 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-galvani-text mb-1">
                        Cookies técnicas
                      </h4>
                      <p className="font-body text-xs text-galvani-textLight leading-relaxed">
                        Imprescindibles para el funcionamiento de la web (navegación, sesión, seguridad).
                        No requieren consentimiento.
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-green-600 flex items-center gap-1 flex-shrink-0">
                      <Check size={14} />
                      Siempre activas
                    </span>
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-white border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-galvani-text mb-1">
                        Cookies de análisis
                      </h4>
                      <p className="font-body text-xs text-galvani-textLight leading-relaxed">
                        Google Analytics y Google Tag Manager. Nos permiten conocer el número de visitas
                        y las páginas más populares de forma anónima.
                      </p>
                    </div>
                    <Switch
                      checked={preferences.analytics}
                      onCheckedChange={(v) => setPreferences((p) => ({ ...p, analytics: v }))}
                      data-testid="cookie-analytics-switch"
                    />
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-white border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-galvani-text mb-1">
                        Cookies de marketing
                      </h4>
                      <p className="font-body text-xs text-galvani-textLight leading-relaxed">
                        Permiten mostrarte publicidad y ofertas personalizadas en base a tu navegación
                        (Meta Pixel, LinkedIn Insight, etc.).
                      </p>
                    </div>
                    <Switch
                      checked={preferences.marketing}
                      onCheckedChange={(v) => setPreferences((p) => ({ ...p, marketing: v }))}
                      data-testid="cookie-marketing-switch"
                    />
                  </div>
                </div>

                <div className="text-xs text-galvani-textLight pt-2">
                  Más información en nuestra{" "}
                  <a
                    href="/politica-cookies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-galvani-purple hover:underline font-medium"
                  >
                    Política de Cookies
                  </a>{" "}
                  y{" "}
                  <a
                    href="/politica-privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-galvani-purple hover:underline font-medium"
                  >
                    Política de Privacidad
                  </a>
                  .
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 md:px-8 py-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-slate-300 text-galvani-text hover:bg-slate-50 font-body font-semibold rounded-full h-11 px-6"
                  data-testid="cookie-settings-reject-btn"
                >
                  Rechazar todas
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  variant="outline"
                  className="border-galvani-purple text-galvani-purple hover:bg-purple-50 font-body font-semibold rounded-full h-11 px-6"
                  data-testid="cookie-settings-save-btn"
                >
                  Guardar preferencias
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold rounded-full h-11 px-6 shadow-md"
                  data-testid="cookie-settings-accept-all-btn"
                >
                  Aceptar todas
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating re-open trigger (visible only once consent is given and banner is hidden) */}
      {view === "hidden" && getCookieConsent() && (
        <button
          onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
          className="fixed bottom-24 md:bottom-4 right-4 md:left-4 md:right-auto z-50 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-galvani-purple hover:scale-110 transition-transform"
          aria-label="Configuración de cookies"
          data-testid="cookie-settings-trigger"
        >
          <Cookie size={18} />
        </button>
      )}
    </>
  );
}
